import { useMemo, useState } from "react";
import { RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";
import { api, errMsg } from "../../lib/api";
import { DICT } from "../../i18n/dict";
import { getPath, useSiteTexts } from "../../i18n/lang";

const GROUPS = {
  nav: "Menu", hero: "Home — Apertura", manifesto: "Home — Manifesto", selection: "Home — Selezione pietre",
  vision: "Home — Una pietra. La tua visione", objects: "Home — Oggetti", processHome: "Home — Processo", closing: "Home — Chiusura",
  archive: "Archivio pietre", detail: "Scheda pietra", bespoke: "Pagina Oggetti su misura", process: "Pagina Processo",
  about: "Pagina Chi siamo", contact: "Modulo richiesta", footer: "Footer", legalDocs: "Pagine legali (privacy, cookie, note legali)",
  cookie: "Banner cookie", common: "Testi comuni", avail: "Disponibilità", form: "Formati", colors: "Colori", finishes: "Finiture",
  notFound: "Pagina 404", seo: "SEO — titoli e descrizioni per Google",
};

const walk = (obj, prefix = "") => Object.entries(obj).flatMap(([k, v]) => {
  const p = prefix ? `${prefix}.${k}` : k;
  if (typeof v === "string") return [p];
  if (v && typeof v === "object") return walk(v, p);
  return [];
});

const FIELDS = Object.keys(GROUPS).map((g) => [g, walk(DICT.en[g], g)]);
const pretty = (path) => path.split(".").slice(1).map((s) => (/^\d+$/.test(s) ? `#${Number(s) + 1}` : s)).join(" › ") || path;

const TextCell = ({ lang, path, current, def, onSaved }) => {
  const [val, setVal] = useState(current ?? def);
  const save = async () => {
    const v = val.trim();
    if (v === (current ?? def)) return;
    try {
      if (!v || v === def) { await api.delete(`/admin/site-texts/${lang}/${path}`); setVal(def); }
      else await api.put("/admin/site-texts", { lang, key: path, value: v });
      toast.success("Testo salvato");
      onSaved();
    } catch (e) { toast.error(errMsg(e, "Errore di salvataggio")); }
  };
  return (
    <textarea className={`field text-sm resize-y ${current ? "!border-b-[#1A1A1A]" : ""}`} rows={Math.min(6, Math.max(1, Math.ceil((val || "").length / 70)))}
      value={val} onChange={(e) => setVal(e.target.value)} onBlur={save} data-testid={`text-${lang}-${path}`} />
  );
};

export const TextsAdmin = () => {
  const qc = useQueryClient();
  const { data = {}, isLoading } = useSiteTexts();
  const [q, setQ] = useState("");
  const refresh = () => qc.invalidateQueries({ queryKey: ["site-texts"] });
  const ql = q.trim().toLowerCase();

  const groups = useMemo(() => FIELDS.map(([g, paths]) => [g, ql ? paths.filter((p) => [p, getPath(DICT.en, p), getPath(DICT.it, p), data.en?.[p], data.it?.[p]].some((x) => (x || "").toLowerCase().includes(ql))) : paths]).filter(([, ps]) => ps.length), [ql, data]);

  const reset = async (p) => {
    await Promise.all(["en", "it"].filter((l) => data[l]?.[p]).map((l) => api.delete(`/admin/site-texts/${l}/${p}`)));
    refresh();
  };

  if (isLoading) return <p className="f-mono text-xs">…</p>;
  const count = Object.keys(data.en || {}).length + Object.keys(data.it || {}).length;

  return (
    <section data-testid="admin-texts">
      <p className="text-sm text-[#555555] max-w-2xl">Modifica i testi del sito in inglese e italiano. Il testo si salva quando esci dal campo. Svuotando un campo torna il testo originale. Testi personalizzati: {count}.</p>
      <input className="field !py-2 text-sm max-w-sm mt-6" placeholder="Cerca una parola o una frase" value={q} onChange={(e) => setQ(e.target.value)} data-testid="texts-search" />
      <div className="mt-8 border-t border-[#1A1A1A]/12">
        {groups.map(([g, paths]) => (
          <details key={g} open={!!ql} className="border-b border-[#1A1A1A]/12 group" data-testid={`texts-group-${g}`}>
            <summary className="cursor-pointer py-5 flex items-center justify-between f-serif text-2xl font-light list-none">
              {GROUPS[g]}<span className="f-mono text-xs text-[#555555]">{paths.length}</span>
            </summary>
            <div className="pb-8">
              <div className="hidden md:grid grid-cols-12 gap-6 eyebrow !text-[0.6rem] text-[#555555] pb-2"><span className="col-span-2">Campo</span><span className="col-span-5">English</span><span className="col-span-5">Italiano</span></div>
              {paths.map((p) => {
                const modified = data.en?.[p] || data.it?.[p];
                return (
                  <div key={p} className="grid grid-cols-1 md:grid-cols-12 gap-x-6 gap-y-1 py-3 border-t border-[#1A1A1A]/8">
                    <div className="md:col-span-2 pt-3 text-xs text-[#555555] f-mono break-words">
                      {pretty(p)}
                      {modified && <button type="button" onClick={() => reset(p)} className="mt-2 flex items-center gap-1 underline underline-offset-2 text-[#1A1A1A]" data-testid={`text-reset-${p}`}><RotateCcw className="w-3 h-3" />Originale</button>}
                    </div>
                    {["en", "it"].map((l) => (
                      <div key={l} className="md:col-span-5">
                        <span className="md:hidden eyebrow !text-[0.58rem] text-[#555555]">{l === "en" ? "English" : "Italiano"}</span>
                        <TextCell key={`${l}-${data[l]?.[p] ?? ""}`} lang={l} path={p} current={data[l]?.[p]} def={getPath(DICT[l], p) ?? ""} onSaved={refresh} />
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>
          </details>
        ))}
      </div>
    </section>
  );
};
